// ============================================================
// assets/event-manager.js
// ============================================================

/**
 * 1. Backend API Client
 */
class EventApiClient {
    constructor(customerId, eventId) {
        this.baseUrl = (window.routes && window.routes.api_url) ? window.routes.api_url : '';
        this.customerId = String(customerId);
        this.eventId = eventId;
    }

    async _fetch(endpoint, options = {}) {
        const url = `${this.baseUrl}${endpoint}`;
        const config = {
            mode: 'cors',
            ...options,
            headers: {
                'Content-Type': 'application/json',
                ...options.headers
            }
        };

        try {
            const res = await fetch(url, config);
            const json = await res.json();
            if (!res.ok) throw new Error(json.message || `API Error ${res.status}`);
            return json;
        } catch (err) {
            console.error(`[API Fail] ${endpoint}:`, err);
            throw err;
        }
    }

    async checkIn() {
        return this._fetch('/event/check-in', {
            method: 'POST',
            body: JSON.stringify({
                customerId: this.customerId,
                eventId: this.eventId,
                missionId: 'mission_attendance'
            })
        });
    }

    async exchangeReward(rewardId) {
        return this._fetch('/event/reward-exchange', {
            method: 'POST',
            body: JSON.stringify({
                customerId: this.customerId,
                eventId: this.eventId,
                rewardId: rewardId
            })
        });
    }

    async getHistory(limit = 1, lastKey = null) {
        const params = new URLSearchParams({
            customerId: this.customerId,
            eventId: this.eventId,
            limit: limit
        });
        if (lastKey) params.append('lastKey', lastKey);
        return this._fetch(`/event/history?${params.toString()}`, { method: 'GET' });
    }
}

/**
 * 2. Event State Manager
 */
class EventStateManager {
    constructor() {
        this.state = {
            isLoggedIn: false,
            config: {
                settings: {},
                missions: [],
                rewards: [],
                gacha: {}
            },
            user: {
                id: null,
                currentStamps: 0,
                claimedRewards: [],
                attendanceHistory: []
            }
        };
    }

    _parseJson(elementId) {
        const el = document.getElementById(elementId);
        if (!el) return null;
        try { return JSON.parse(el.textContent); }
        catch (e) { return null; }
    }

    initialize(ids) {
        this.state.config.settings = this._parseJson(ids.settingsId) || {};
        this.state.config.missions = this._parseJson(ids.missionsId) || [];
        this.state.config.rewards = this._parseJson(ids.rewardsId) || [];

        // Fallback
        if (this.state.config.rewards.length === 0) {
            this.state.config.rewards = [
                { id: 'reward_shipping', cost_stamps: 5, limit_per_user: 1 },
                { id: 'reward_product_100', cost_stamps: 10, limit_per_user: 1 },
                { id: 'reward_coupon_20k', cost_stamps: 15, limit_per_user: 1 },
                { id: 'reward_coupon_30k', cost_stamps: 30, limit_per_user: 1 }
            ];
        }

        this.state.config.gacha = this._parseJson(ids.gachaId) || {};

        const rawCustomerData = this._parseJson(ids.customerDataId);
        const eventHandle = ids.eventHandle;

        if (rawCustomerData && rawCustomerData.customer_id) {
            this.state.isLoggedIn = true;
            this.state.user.id = rawCustomerData.customer_id;

            const eventHistory = rawCustomerData.events && rawCustomerData.events[eventHandle];
            if (eventHistory) {
                this.state.user.currentStamps = eventHistory.current_stamps || 0;
                this.state.user.claimedRewards = eventHistory.rewards?.claimed_ids || [];

                if (eventHistory.mission_progress && eventHistory.mission_progress.mission_attendance) {
                    this.state.user.attendanceHistory = eventHistory.mission_progress.mission_attendance.history || [];
                }
            }
        }
    }

    get stamps() { return this.state.user.currentStamps; }
    getRewards() { return this.state.config.rewards; }
    getRewardById(rewardId) { return this.state.config.rewards.find(r => r.id == rewardId); }

    canRedeem(rewardId) {
        // 가챠(gacha)인 경우 별도 로직 (Config에 'gacha' 관련 항목이 없으면 기본 비용 체크만 수행)
        if (rewardId === 'reward_gacha') {
            const gachaCost = this.state.config.gacha ? (this.state.config.gacha.cost_stamps || 1) : 1;
            if (this.stamps < gachaCost) return { possible: false, reason: `코인이 부족합니다. (필요: ${gachaCost}, 보유: ${this.stamps})` };
            return { possible: true };
        }

        const reward = this.getRewardById(rewardId);
        if (!reward) return { possible: false, reason: "존재하지 않는 상품입니다." };
        if (this.stamps < reward.cost_stamps) return { possible: false, reason: `코인이 부족합니다. (필요: ${reward.cost_stamps}, 보유: ${this.stamps})` };
        if (this.state.user.claimedRewards.includes(String(rewardId))) return { possible: false, reason: "이미 교환한 선물입니다." };
        return { possible: true };
    }

    updateUserBalance(newBalance) { this.state.user.currentStamps = newBalance; }
    addClaimedReward(rewardId) {
        const idStr = String(rewardId);
        if (!this.state.user.claimedRewards.includes(idStr)) this.state.user.claimedRewards.push(idStr);
    }
    addAttendanceToday() {
        const today = new Date().toISOString().split('T')[0];
        if (!this.state.user.attendanceHistory.includes(today)) this.state.user.attendanceHistory.push(today);
    }
}

/**
 * 3. Event Controller
 */
class EventController {
    constructor(config = {}) {
        this.store = new EventStateManager();
        this.api = null;
        this.config = {
            eventHandle: '2026-new-year',
            settingsId: 'liquid-settings-data',
            missionsId: 'liquid-missions-data',
            rewardsId: 'liquid-rewards-data',
            gachaId: 'liquid-gacha-data',
            customerDataId: 'liquid-customer-data',
            ...config
        };
    }

    init() {
        this.store.initialize(this.config);
        if (this.store.state.isLoggedIn) {
            this.api = new EventApiClient(this.store.state.user.id, this.config.eventHandle);
        }
    }

    async tryCheckIn() {
        if (!this.api) return alert("로그인이 필요합니다.");
        try {
            const res = await this.api.checkIn();
            const newBalance = (res.new_balance !== undefined) ? res.new_balance : res.current_stamps;
            if (res.success || newBalance !== undefined) {
                if (newBalance !== undefined) this.store.updateUserBalance(newBalance);
                this.store.addAttendanceToday();
                return true;
            } else {
                alert("이미 출석했습니다.");
                return false;
            }
        } catch (e) {
            alert("출석체크 중 오류가 발생했습니다.");
            throw e;
        }
    }

    // ✅ [수정] isGacha 파라미터 추가
    async tryExchange(rewardId, isGacha = false) {
        if (!this.api) { alert("로그인이 필요합니다."); return false; }

        const check = this.store.canRedeem(rewardId);
        if (!check.possible) { alert(check.reason); return false; }

        try {
            const res = await this.api.exchangeReward(rewardId);

            // 성공 처리 분기
            if (res.result.success) {
                // 일반 선물이면 '교환완료' 처리를 위해 저장 (가챠는 중복 가능할 수 있으므로 로직에 따라 다름)
                if (!isGacha) {
                    this.store.addClaimedReward(rewardId);
                }

                // [분기 1] 가챠인 경우: 결과 데이터 반환 (UI는 호출한 곳에서 처리)
                if (isGacha) {
                    return { success: true, data: res.result };
                }

                // [분기 2] 일반 선물인 경우: 공통 성공 모달 띄우기
                else {
                    const modal = document.getElementById("reward-success-modal");
                    const titleEl = document.getElementById("reward-success-popup-title");
                    const descEl = document.getElementById("reward-success-popup-desc");
                    const reward = this.store.getRewardById(rewardId);
                    const cost = reward ? reward.cost_stamps : 0;
                    const rewardTitle = res.result.title || "선물";

                    if (titleEl) titleEl.innerText = `${rewardTitle} 발급 완료`;
                    if (descEl) descEl.innerHTML = `코인 ${cost}개로 ${rewardTitle} 교환을 완료하였습니다.`;
                    if (modal && modal.open) modal.open();
                    return true;
                }
            } else {
                alert("교환에 실패했습니다.");
                return false;
            }
        } catch (e) {
            alert("일시적인 오류가 발생했습니다. 잠시 후 다시 시도해주세요.");
            throw e;
        }
    }
}

/**
 * 4. Main Page Logic with jQuery UI Integration
 */
document.addEventListener('DOMContentLoaded', async () => {
    // 1. Controller Init
    const eventHandle = window.IPX_EVENT_HANDLE || '2026-new-year';
    const controller = new EventController({ eventHandle: eventHandle });
    controller.init();

    // =========================================================
    // ✅ UI Helper Functions (jQuery Based)
    // =========================================================

    // [UI] 선물 교환 버튼 Dim 처리
    function updateEtcButtonsDim() {
        const $items = $(".sc_gift .etc_list li[data-state]");
        if (!$items.length) return;

        $items.each(function () {
            const $item = $(this);
            const state = $item.data("data-state");
            const $btn = $item.find(".btn_wrap button");
            const $icon = $btn.find("img");

            if (state === "done" || state === "soldout" || state === "disabled") {
                $icon.attr("src", "https://vos.line-scdn.net/ipx-mall/images/out/showcase/251216/img/pc/coin_mini_dimmed.png");
                $btn.prop("disabled", true);
            } else {
                $icon.attr("src", "https://vos.line-scdn.net/ipx-mall/images/out/showcase/251216/img/pc/coin_mini.png");
                $btn.prop("disabled", false);
            }
        });
    }

    // [UI] 가챠 버튼 Dim 처리
    function updateGachaBtnDim() {
        const $wrap = $(".sc_gift .gacha_btn");
        if (!$wrap.length) return;

        $wrap.each(function () {
            const $btnWrap = $(this);
            // ⭐ [핵심 수정] .data() 대신 .attr()을 써야 변경된 값을 즉시 가져옵니다.
            const state = $btnWrap.attr("data-state");
            const $btn = $btnWrap.find("button");
            const $icon = $btn.find("img");
            const $text = $btn.find("span");

            if (state === "soldout" || state === "disabled") {
                $icon.attr("src", "https://vos.line-scdn.net/ipx-mall/images/out/showcase/251216/img/pc/coin_mini_dimmed.png");
                $btn.prop("disabled", true);
                $text.css("color", "#A0A0A0");
            } else {
                $icon.attr("src", "https://vos.line-scdn.net/ipx-mall/images/out/showcase/251216/img/pc/coin_mini.png");
                $btn.prop("disabled", false);
                $text.css("color", "");
            }
        });
    }

    // [UI] 이미지 경로 교체 (PC <-> MO)
    function updateResponsiveImages() {
        const BREAKPOINT = 990;
        const isMobile = window.innerWidth <= BREAKPOINT;

        // 1. 출석체크 이미지
        $(".attendance_days .day_item img").each(function () {
            const $img = $(this);
            const src = $img.attr("src");
            if (!src) return;
            if (isMobile && src.includes("/pc/")) $img.attr("src", src.replace("/pc/", "/mo/"));
            if (!isMobile && src.includes("/mo/")) $img.attr("src", src.replace("/mo/", "/pc/"));
        });

        // 2. 가챠 결과 팝업 이미지
        $(".gacha_popup .result_img img").each(function () {
            const $img = $(this);
            const src = $img.attr("src");
            if (!src) return;
            if (isMobile && src.includes("/pc/")) $img.attr("src", src.replace("/pc/", "/mo/"));
            if (!isMobile && src.includes("/mo/")) $img.attr("src", src.replace("/mo/", "/pc/"));
        });
    }

    // =========================================================
    // ✅ Logic & UI Binding
    // =========================================================

    // 상태에 따른 UI 업데이트
    const updateButtonsUI = () => {
        const currentStamps = controller.store.stamps || 0;
        const claimedRewardIds = (controller.store.state.user.claimedRewards || []).map(String);
        const allRewards = controller.store.getRewards();
        const gachaConfig = controller.store.state.config.gacha;

        // 코인 개수 업데이트
        const coinEl = document.getElementById('display-coin-count');
        if (coinEl) coinEl.innerText = `${currentStamps}개`;

        // 플로팅 바(PC/MO) 코인 개수 업데이트
        // 클래스가 .coin_count인 모든 요소를 찾아 숫자를 업데이트합니다.
        document.querySelectorAll('.coin_count').forEach(el => {
            el.innerText = currentStamps;
        });

        // 1. 선물 교환 버튼 상태 결정
        allRewards.forEach(reward => {
            const button = document.querySelector(`.btn_exchange[data-reward-id="${reward.id}"]`);
            if (!button) return;

            const li = button.closest('li');
            let newState = 'disabled';
            let newHtml = button.innerHTML;

            if (claimedRewardIds.includes(String(reward.id))) {
                newState = 'done';
                newHtml = '<span>교환 완료</span>';
            }
            else if (currentStamps >= reward.cost_stamps) {
                newState = 'default';
                if(!button.querySelector('img')) {
                    newHtml = `<img src="https://vos.line-scdn.net/ipx-mall/images/out/showcase/251216/img/pc/coin_mini.png" alt=""> <span>✕ ${reward.cost_stamps}개로 교환하기</span>`;
                }
            }
            else {
                newState = 'disabled';
                if (!button.querySelector('img')) {
                    newHtml = `<img src="https://vos.line-scdn.net/ipx-mall/images/out/showcase/251216/img/pc/coin_mini.png" alt=""> <span>✕ ${reward.cost_stamps}개로 교환하기</span>`;
                }
            }

            if (li) li.setAttribute('data-state', newState);
            button.innerHTML = newHtml;
        });

        // 2. 가챠 버튼 상태 결정
        const gachaBtn = document.querySelector('[data-action="gacha"]');
        if (gachaBtn) {
            const gachaCost = gachaConfig ? (gachaConfig.cost_stamps || 1) : 1;
            const canGacha = currentStamps >= gachaCost;

            const gachaWrap = gachaBtn.closest('.gacha_btn');
            if (gachaWrap) {
                gachaWrap.setAttribute('data-state', canGacha ? 'default' : 'disabled');
            }
        }

        // 3. jQuery UI 갱신
        updateEtcButtonsDim();
        updateGachaBtnDim();
    };

    // [JS] 미션 카운트 UI 업데이트 헬퍼 함수 추가
    function updateMissionCountUI(missionKey, count) {
        // 1. 해당 미션의 li 요소 찾기
        const $li = $(`.mission_content li[data-mission="${missionKey}"]`);
        if (!$li.length) return;

        // 2. 카운트 숫자 업데이트
        const $countNum = $li.find('[data-role="count"]');
        $countNum.text(count);

        // 3. 부모 .mission_count 요소의 클래스(is-active / is-zero) 및 텍스트(!) 처리
        const $missionCountEl = $li.find('.mission_count');

        // 기존 내용 지우고 새로 구성 (느낌표 처리 때문)
        if (count > 0) {
            $missionCountEl.removeClass('is-zero').addClass('is-active');
            // HTML 구조 재설정 (숫자 뒤에 느낌표 붙이기)
            $missionCountEl.html(`현재까지 <span class="count_num" data-role="count">${count}</span>개 획득!`);
        } else {
            $missionCountEl.removeClass('is-active').addClass('is-zero');
            // HTML 구조 재설정 (느낌표 없음)
            $missionCountEl.html(`현재까지 <span class="count_num" data-role="count">${count}</span>개 획득`);
        }
    }

    // [JS] 출석체크 성공 시 UI 업데이트 함수
    const updateAttendanceUI = () => {
        // 1. 팝업 내 캘린더 날짜 갱신 (기존 로직)
        const today = new Date().getDate();
        const dayEl = document.querySelector(`.day_item.day_${today}`);
        if (dayEl) dayEl.setAttribute('data-state', 'done');

        const daysEl = document.querySelector('.attendance_popup .days');
        let newCount = 1; // 기본값

        if (daysEl) {
            // "N일"에서 숫자만 추출
            const currentDays = parseInt(daysEl.innerText.replace(/[^0-9]/g, '')) || 0;
            newCount = currentDays + 1;
            daysEl.innerText = `${newCount}일`;
        }

        // 2. [수정됨] 메인 화면의 '출석체크' 카운트 텍스트 업데이트
        const $mainCountDisplay = $('#attendance-count-display'); // jQuery 사용 (Liquid에서 id 추가함)

        if ($mainCountDisplay.length) {
            // 스타일 변경: 0개(회색) -> 1개 이상(핑크)
            $mainCountDisplay.removeClass('is-zero').addClass('is-active');

            // 텍스트 변경: 느낌표(!) 포함
            $mainCountDisplay.html(`현재까지 <span class="count_num" data-role="attendance_count">${newCount}</span>개 획득!`);
        }

        // 3. '출석체크 하기' 버튼 상태 변경 (기존 로직)
        const confirmBtn = document.querySelector('[data-action="confirm_attendance"]');
        if (confirmBtn) {
            confirmBtn.setAttribute('data-state', 'done_today');
            confirmBtn.innerText = '오늘 출석 완료';
            confirmBtn.disabled = true; // 버튼 비활성화
        }
    };

    const fetchLatestStatus = async () => {
        if (!controller.api) {
            const coinEl = document.getElementById('display-coin-count');
            if(coinEl) coinEl.innerText = "0개";

            // [추가] 플로팅 바 코인 0으로 초기화
            document.querySelectorAll('.coin_count').forEach(el => {
                el.innerText = "0";
            });

            updateButtonsUI();
            return;
        }

        try {
            const response = await controller.api.getHistory(1);
            if (response && typeof response.current_stamps !== 'undefined') {
                controller.store.updateUserBalance(response.current_stamps);
                updateButtonsUI();
            }
        } catch (error) {
            console.error("[API] 스탬프 동기화 실패:", error);
        }
    };

    // --- [히스토리 렌더링] ---
    const renderHistory = (historyList) => {
        const tbody = document.querySelector('.history_table--body tbody');
        if (!tbody) return;
        tbody.querySelectorAll('.history_row').forEach(row => row.remove());
        const emptyMsg = tbody.querySelector('.history_empty');

        if (!historyList || historyList.length === 0) {
            if(emptyMsg) emptyMsg.style.display = 'table-row';
        } else {
            if(emptyMsg) emptyMsg.style.display = 'none';
            const baseUrl = "https://vos.line-scdn.net/ipx-mall/images/out/showcase/251216/img/pc";

            historyList.forEach(item => {
                const tr = document.createElement('tr');
                tr.className = 'history_row';
                const date = item.timestamp ? item.timestamp.split('T')[0] : (item.created_at ? item.created_at.split('T')[0] : '-');
                const absAmount = Math.abs(item.amount);
                let coinDisplay = `${absAmount}`;
                let badgeType = 'type_exchange';
                let badgeText = '사용';
                let imgSrc = `${baseUrl}/gift_box.png`;

                if (item.type === 'gacha_redeem') {
                    badgeType = 'type_gacha'; badgeText = '가챠'; imgSrc = `${baseUrl}/lucky_ball.png`;
                } else if (item.type === 'redeem') {
                    badgeType = 'type_exchange'; badgeText = '교환'; imgSrc = `${baseUrl}/gift_box.png`;
                } else if (item.amount > 0) {
                    badgeType = 'type_gacha'; badgeText = '적립'; imgSrc = `${baseUrl}/coin_mini.png`; coinDisplay = `+${absAmount}`;
                }

                tr.innerHTML = `
                    <td class="td_info">
                        <div class="item_img"><img src="${imgSrc}" alt="" /></div>
                        <div class="item_text">
                            <span class="item_badge ${badgeType}">${badgeText}</span>
                            <span class="item_name">${item.description || item.title || '이벤트 참여'}</span>
                        </div>
                    </td>
                    <td class="td_date">${date}</td>
                    <td class="td_coin">${coinDisplay}</td>
                `;
                tbody.appendChild(tr);
            });
        }
        if (window.updateCoinPopupScroll) window.updateCoinPopupScroll();
    };

    // =========================================================
    // ✅ Event Listeners & Interaction Init
    // =========================================================

    // 1. 출석체크 로직
    // const confirmAttendanceBtn = document.querySelector('[data-action="confirm_attendance"]');
    // if (confirmAttendanceBtn) {
    //     confirmAttendanceBtn.addEventListener('click', async (e) => {
    //         try {
    //             const isSuccess = await controller.tryCheckIn();
    //             if (isSuccess) {
    //                 await fetchLatestStatus(); // 잔액 갱신
    //                 updateAttendanceUI(); // UI 반영
    //             }
    //         } catch (e) { console.error(e); }
    //     });
    // }
    // =========================================================
    // ✅ Event Listeners & Interaction Init
    // =========================================================

    // 1. 출석체크 로직 (수정됨: 로딩 상태 추가)
    const confirmAttendanceBtn = document.querySelector('[data-action="confirm_attendance"]');
    if (confirmAttendanceBtn) {
        confirmAttendanceBtn.addEventListener('click', async (e) => {
            e.preventDefault(); // 기본 동작 방지

            // 이미 처리 중이거나 비활성화 상태면 무시
            if (confirmAttendanceBtn.disabled) return;

            // 1. 기존 텍스트 저장 (실패 시 원복용)
            const originalText = confirmAttendanceBtn.innerText;

            // 2. 로딩 상태 적용 (비활성화 + 텍스트 변경)
            confirmAttendanceBtn.disabled = true;
            confirmAttendanceBtn.innerText = '처리중...';

            try {
                // 3. API 호출
                const isSuccess = await controller.tryCheckIn();

                if (isSuccess) {
                    // 성공 시: 데이터 갱신 및 완료 UI 처리
                    // (updateAttendanceUI 함수가 버튼을 '오늘 출석 완료' 상태로 바꿉니다)
                    await fetchLatestStatus();
                    updateAttendanceUI();
                } else {
                    // 로직 실패(이미 출석함 등) 시: 원복
                    confirmAttendanceBtn.disabled = false;
                    confirmAttendanceBtn.innerText = originalText;
                }
            } catch (err) {
                console.error(err);
                // 에러 발생 시: 버튼 원상 복구
                confirmAttendanceBtn.disabled = false;
                confirmAttendanceBtn.innerText = originalText;
                alert("일시적인 오류가 발생했습니다. 잠시 후 다시 시도해주세요.");
            }
        });
    }

    // =========================================================
    // ✅ [수정됨] 2. 일반 선물 교환 버튼 (data-state 제어 + 즉시 UI 반영)
    // =========================================================
    document.querySelectorAll('.btn_exchange').forEach(btn => {
        btn.addEventListener('click', async (e) => {
            e.preventDefault();
            const targetBtn = e.currentTarget;
            const li = targetBtn.closest('li');
            const rewardId = targetBtn.dataset.rewardId;

            if (!rewardId) return;

            // [중복 클릭 방지] li의 상태가 disabled(처리중) 이거나 done(완료)이면 무시
            if (li && (li.getAttribute('data-state') === 'disabled' || li.getAttribute('data-state') === 'done')) {
                return;
            }

            // 실패 시 원복을 위해 현재 상태 저장
            const originalState = li ? li.getAttribute('data-state') : 'default';
            const originalHtml = targetBtn.innerHTML;

            // 1. [상태 변경] '처리중' 상태로 변경
            if (li) li.setAttribute('data-state', 'disabled');
            targetBtn.innerHTML = '<span>처리중...</span>';

            // 2. [UI 갱신] 즉시 이미지를 Dim 처리 (중요)
            updateEtcButtonsDim();

            try {
                // 3. API 호출
                const isSuccess = await controller.tryExchange(rewardId, false);

                if (isSuccess) {
                    // 성공 시: 전체 상태 갱신 (서버 데이터에 따라 'done' 등으로 바뀜)
                    await fetchLatestStatus();
                } else {
                    // 실패(로직 오류 등) 시: 원상 복구
                    targetBtn.innerHTML = originalHtml;
                    if (li) li.setAttribute('data-state', originalState);
                    updateEtcButtonsDim(); // 원복된 상태 반영
                }
            } catch (err) {
                console.error("Exchange Error:", err);
                // 에러 발생 시: 원상 복구
                targetBtn.innerHTML = originalHtml;
                if (li) li.setAttribute('data-state', originalState);
                updateEtcButtonsDim(); // 원복된 상태 반영
            }
        });
    });

    // 3. 코인 히스토리 조회
    const btnHistory = document.getElementById('btn-history');
    if (btnHistory) {
        btnHistory.addEventListener('click', async () => {
            if (!controller.api) return alert('로그인이 필요합니다.');
            try {
                const res = await controller.api.getHistory(50);
                renderHistory(res.history || []);
            } catch (e) { console.error('히스토리 로드 실패:', e); }
        });
    }

    // 4. 모달 닫기 이벤트
    const successModalCloseBtn = document.querySelector("#reward-success-modal .modal-close");
    if (successModalCloseBtn) {
        successModalCloseBtn.addEventListener("click", () => {
            const modal = document.getElementById("reward-success-modal");
            if (modal && modal.close) modal.close();
        });
    }
    const couponBtn = document.getElementById("modal-reward-coupon-button");
    if(couponBtn) {
        couponBtn.addEventListener("click", () => location.href = '/pages/coupon');
    }

    // [Init] 반응형 이미지 리사이즈 리스너 등록
    updateResponsiveImages();
    $(window).on("resize", updateResponsiveImages);

    // [Popup] 캘린더(출석) 팝업 제어
    const $attendancePopup = $('.attendance_popup[data-popup="attendance_popup"]');
    if ($attendancePopup.length) {
        const $openBtn = $(".sc_mission .attendance_btn button");
        const $closeBtn = $attendancePopup.find(".popup_close");

        const openAttendancePopup = () => { $attendancePopup.show(); $("body").addClass("is-popup-open"); };
        const closeAttendancePopup = () => { $attendancePopup.hide(); $("body").removeClass("is-popup-open"); };

        $openBtn.on("click", (e) => { e.preventDefault(); openAttendancePopup(); });
        $closeBtn.on("click", (e) => { e.preventDefault(); closeAttendancePopup(); });
        $attendancePopup.on("click", ".dimmed_wrap.mo_only", (e) => { e.preventDefault(); closeAttendancePopup(); });
    }

    // =========================================================
    // ✅ [수정됨] Gacha: 클릭 시 data-state 변경 후 '즉시 UI 반영' 추가
    // =========================================================
    (function initGachaFlow() {
        const $gachaPopup = $('.gacha_popup[data-popup="gacha_result"]');
        const $motionWrap = $(".sc_gift .motion_wrap");
        if (!$motionWrap.length) return;


        // ✅ 251231 추가 : lottie 파티클 시작
        const LOTTIE_PATH =
        "https://vos.line-scdn.net/ipx-mall/images/out/showcase/251223/js/particles_01.json";
        let particlesAnim = null;

        function getLottieLib() {
        return window.lottie || window.bodymovin || null;
        }

        function ensureParticles() {
        const lottie = getLottieLib();
        if (!lottie) return null;
        if (!$gachaPopup.length) return null;

        const container = $gachaPopup.find('[data-role="gacha_particles"]')[0];
        if (!container) return null;

        if (particlesAnim) return particlesAnim;

        container.innerHTML = "";
        particlesAnim = lottie.loadAnimation({
            container,
            renderer: "svg",
            loop: false,
            autoplay: false,
            path: LOTTIE_PATH,
        });

        return particlesAnim;
        }

        function playParticles() {
        const anim = ensureParticles();
        if (!anim) return;
        anim.stop();
        anim.goToAndPlay(0, true);
        }

        ensureParticles();
        // ✅ 251231 추가 : lottie 파티클 끝


        const openGachaPopup = () => {
            if (!$gachaPopup.length) return;

            $gachaPopup.show();

            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                playParticles(); // ✅ 251231 추가 : lottie 파티클 실행
                });
            });
        };

        const closeGachaPopup = () => {
            if (!$gachaPopup.length) return;
            if (particlesAnim) particlesAnim.stop(); // ✅ 251231 추가 : 닫을 때 로티 정지
            $gachaPopup.hide();
        };


        $(document).on("click", ".sc_gift .gacha_btn button", async function (e) {
            e.preventDefault();
            const $thisBtn = $(this);
            const $btnWrap = $thisBtn.closest(".gacha_btn");
            const $textSpan = $thisBtn.find("span");

            // 1. 현재 상태 체크
            const currentState = $btnWrap.attr("data-state");

            // 이미 품절이거나, 이미 처리중(disabled)이면 클릭 무시
            if (currentState === "soldout" || currentState === "disabled") return;

            const originalState = currentState || 'default';
            const originalHtml = $textSpan.html();

            // 2. 상태 변경 (Lock)
            $btnWrap.attr("data-state", "disabled");
            // ✅ [추가] 텍스트 변경
            $textSpan.text("처리중...");

            // ⭐ [핵심 수정] 상태를 바꿨으니, 이미지를 교체하는 함수를 여기서 바로 호출해야 합니다!
            if (typeof updateGachaBtnDim === 'function') {
                updateGachaBtnDim();
            }

            // 3. GIF 재생 시작
            $motionWrap.addClass("is-playing");

            try {
                // 4. API 호출 + 5초 대기
                const gachaPromise = controller.tryExchange('reward_gacha', true);
                const delayPromise = new Promise(resolve => setTimeout(resolve, 5000));

                const [response, _] = await Promise.all([gachaPromise, delayPromise]);

                // 5. GIF 재생 종료
                $motionWrap.removeClass("is-playing");

                // ✅ [추가] API 완료 후(성공이든 실패든) 텍스트 원복
                $textSpan.html(originalHtml);

                if (response && response.success) {
                    const resultData = response.data;

                    // --- 팝업 내용 세팅 (기존 로직 유지) ---
                    const $popupTitle = $gachaPopup.find('[data-role="result_title"]');
                    const $popupBadge = $gachaPopup.find('[data-role="result_badge"]');
                    const $popupImg = $gachaPopup.find('[data-role="result_img"] img');
                    const $btnGift = $gachaPopup.find('[data-action="go_gift"]');
                    const $btnCouponPage = $gachaPopup.find('[data-action="go_coupon"]');

                    let titleHtml = `<span class="bold_span">${resultData.description}</span>`;
                    // if ($popupTitle.length) $popupTitle.html(`<span class="bold_span">${resultData.description}</span>`);

                    const isMobile = window.innerWidth <= 990;
                    const imgPath = isMobile ? 'mo' : 'pc';
                    const baseUrl = `https://vos.line-scdn.net/ipx-mall/images/out/showcase/251216/img/${imgPath}`;

                    let imgFileName = "gacha_result_img_01.png";
                    let badgeText = "당첨!";
                    let resultRank = "1st"; // 기본값

                    switch (resultData.id) {
                        case "item_S_grade": imgFileName = "gacha_result_img_01.png"; badgeText = "1등 당첨!"; resultRank = "1st"; break;
                        case "item_A_grade": imgFileName = "gacha_result_img_02.png"; badgeText = "2등 당첨!"; resultRank = "2nd"; break;
                        case "item_B_grade": imgFileName = "gacha_result_img_03.png"; badgeText = "3등 당첨!"; resultRank = "3rd"; break;
                        case "item_C_grade": imgFileName = "gacha_result_img_04.png"; badgeText = "4등 당첨!"; resultRank = "4th"; break;
                        case "item_miss":    imgFileName = "gacha_result_img_05.png"; badgeText = "꽝"; resultRank = "lose";
                            titleHtml = `<span class="bold_span">꽝! </span>
                                         아쉽지만 <br />다음 기회에 다시 도전하세요!`;
                            break;
                        default:             imgFileName = "gacha_result_img_05.png";
                    }

                    if ($popupTitle.length) $popupTitle.html(titleHtml);
                    // ✅ [추가됨] 팝업의 data-result 속성 업데이트
                    $gachaPopup.attr("data-result", resultRank);

                    if ($popupImg.length) $popupImg.attr("src", `${baseUrl}/${imgFileName}`);
                    if ($popupBadge.length) $popupBadge.text(badgeText);

                    if (resultData.type === 'miss') {
                        $btnGift.hide();
                        if ($btnCouponPage.length) $btnCouponPage.hide();
                    } else {
                        $btnGift.show();
                        if ($btnCouponPage.length) $btnCouponPage.show();
                        $btnGift.attr('href', (resultData.reward_detail && resultData.reward_detail.reference_id) ? resultData.reward_detail.reference_id : '#');
                    }

                    // 팝업 오픈
                    openGachaPopup();

                    // 6. [최종 갱신] 성공했으므로 서버 잔액에 맞춰 버튼 상태 재계산
                    await fetchLatestStatus();

                } else {
                    // API 실패(false) 시 원복
                    $btnWrap.attr("data-state", originalState);
                    if (typeof updateGachaBtnDim === 'function') updateGachaBtnDim(); // 원복 반영
                }

            } catch (err) {
                console.error("Gacha Failed:", err);
                $motionWrap.removeClass("is-playing");

                // 에러 발생 시 원복
                $btnWrap.attr("data-state", originalState);
                $textSpan.html(originalHtml);
                if (typeof updateGachaBtnDim === 'function') updateGachaBtnDim(); // 원복 반영

                alert("일시적인 오류가 발생했습니다. 잠시 후 다시 시도해주세요.");
            }
        });

        // 팝업 닫기
        $(document).on("click", ".gacha_popup .popup_close, .gacha_popup .dimmed_wrap", function (e) {
            e.preventDefault();
            closeGachaPopup();
        });
    })();

    // 초기 실행
    await fetchLatestStatus();



    // ✅ 251231 추가 시작 
    const swiper = new Swiper(".mySwiper", {
        slidesPerView: 1,
        loop: true,

        effect: "fade",
        fadeEffect: { crossFade: true },

        autoHeight: true,

        pagination: {
        el: ".process-pagination",
        clickable: true,
        },
    });

    gsap.registerPlugin(ScrollTrigger);

    // sc_main 모션
    const mainIntroTl = gsap.timeline({
    scrollTrigger: {
      trigger: ".sc_main",
      start: "top 70%",
      once: true,
      // markers: true,
    },
    defaults: {
      ease: "power2.out",
    },
  });

  // 1️⃣ 타이틀
  mainIntroTl.to(".sc_main .main_title_wrap", {
    opacity: 1,
    y: 0,
    duration: 0.6,
  });

  // 2️⃣ 텍스트
  mainIntroTl.to(
    ".sc_main .main_txt_wrap p",
    {
      opacity: 1,
      y: 0,
      duration: 0.45,
      stagger: 0.12,
    },
    "-=0.15"
  );

  // 3️⃣ 이미지들 동시에 등장
  mainIntroTl.to(
    ".sc_main .img_box img",
    {
      opacity: 1,
      duration: 0.6,
    },
    "-=0.4" // ← 타이틀/텍스트랑 거의 동시에
  );

    // sc_coin 모션
    const coinIntroTl = gsap.timeline({
        scrollTrigger: {
        trigger: ".sc_coin",
        start: "top 70%",
        once: true,
        },
        defaults: { ease: "power2.out" },
    });

    coinIntroTl.to(
        [
        ".sc_coin .title_wrap",
        ".sc_coin .txt_wrap",
        ".sc_coin .status_wrap .flex_wrap",
        ],
        {
        opacity: 1,
        y: 0,
        duration: 0.6,
        }
    );

    coinIntroTl.to(
        ".sc_coin .status_wrap",
        {
        opacity: 1,
        duration: 0.01,
        },
        0
    );

    //sc_mission 모션
    const missionIntroTl = gsap.timeline({
        scrollTrigger: {
        trigger: ".sc_mission",
        start: "top 70%",
        once: true,
        },
        defaults: { ease: "power2.out" },
    });

    missionIntroTl.to(
        [
        ".sc_mission .title_wrap",
        ".sc_mission .txt_wrap",
        ".sc_mission .mission_content",
        ],
        {
        opacity: 1,
        y: 0,
        duration: 0.6,
        }
    );

    //sc_gift 모션
    const giftIntroTl = gsap.timeline({
        scrollTrigger: {
        trigger: ".sc_gift",
        start: "top 70%",
        once: true,
        },
        defaults: { ease: "power2.out" },
    });

    giftIntroTl.to(
        [
        ".sc_gift .title_wrap",
        ".sc_gift .txt_wrap",
        ".sc_gift .gift_content",
        ".sc_gift .etc_txt",
        ],
        {
        opacity: 1,
        y: 0,
        duration: 0.6,
        }
    );
  // ✅ 251231 추가 끝
});